// pages/master-data/vehicle/MasterVehiclePage.jsx
'use client';
import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, IconButton, Box } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useMasterVehicleList, deleteVehicle } from '../../../stores/masterVehicleStore';
import { useCompanyList } from '../../../stores/companyStore';
import { useMasterFuelPriceList } from '../../../stores/masterFuelPriceStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import DetailDialog from '../../../components/DetailDialog';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MasterVehicleDetail from './MasterVehicleDetail';
import MasterVehicleAddEdit from './MasterVehicleAddEdit';
import { useLanguage } from '../../../utils/i18n';
import { subscribeRetryQueue } from '../../../helpers/retryQueue';
import { useVtdsList } from '../../../stores/vtdsStore';
import debounce from 'lodash-es/debounce';
import { useNavigate } from 'react-router-dom';

import { fleetApi } from '../../../api/axiosClient';

function MasterVehiclePageContent() {
  const navigate = useNavigate();
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const hasMasterGpsMenu = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes('"modules_path":"master-gps"');
    } catch {
      return false;
    }
  }, []);

  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [searchField, setSearchField] = useState('license_plate');
  // const [filterMaintenance, setFilterMaintenance] = useState('all');

  const [searchRaw, setSearchRaw] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [localVehicles, setLocalVehicles] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'license_plate', label: t('masterVehicle.plateNumber', 'Plate Number') },
      { value: 'vendor', label: t('masterVehicle.vendor', 'Vendor'), tableName: 'm_vendor', fieldName: 'name', joinKey: 'vendor' },
      { value: 'vehicle_type', label: t('general.vehicleType', 'Vehicle Type') },
      { value: 'name', label: t('masterVehicle.bodyName', 'Body Name') }
    ],
    [t]
  );

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    const stored = localStorage.getItem('companyId');
    return stored ? [{ value: stored, label: stored }] : [];
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Warning Dialog State
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  const [openDetail, setOpenDetail] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const notify = useNotifier();

  const { companyOptions } = useCompanyList();
  const { data: vtdsData } = useVtdsList(0, 10000, '', '', '');

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchRaw(value);
        setPaginationModel((p) => ({ ...p, page: 0 }));
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    const stored = localStorage.getItem('companyId');
    if (!stored) return;
    if (companyOptions && companyOptions.length) {
      const match = companyOptions.find((c) => String(c.value) === String(stored));
      if (match) {
        setSelectedCompanies([{ value: String(match.value), label: match.label || match.name || String(match.value) }]);
      } else {
        setSelectedCompanies([{ value: String(stored), label: String(stored) }]);
      }
    }
  }, [companyOptions]);

  useEffect(() => {
    const unsubscribe = subscribeRetryQueue((queue) => {
      console.log('Retry queue size:', queue.length);
    });

    return unsubscribe;
  }, []);
  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchRaw = searchField === 'all' ? '' : searchRaw;

  const {
    data: masterVehiclesFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useMasterVehicleList(paginationModel.page, paginationModel.pageSize, selectedCompanies, hookSearchField, hookSearchRaw);

  const rows = searchField === 'all' ? localVehicles : masterVehiclesFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        // 1. Rakit Filter Perusahaan (columns)
        const columns = [];
        if (selectedCompanies && selectedCompanies.length > 0) {
          const values = selectedCompanies.map((s) => String(s.value));
          if (values.length === 1) {
            columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
          } else {
            columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
          }
        }

        // Default minimal object untuk menghindari error backend jika columns kosong
        if (columns.length === 0) {
          columns.push({ logic_operator: 'like', name: 'license_plate', operator: 'AND', value: '' });
        }

        // 2. Rakit Array 'filters' dinamis untuk pencarian global berdasarkan searchRaw
        let filters = [];
        if (searchRaw) {
          filters = [
            { name: 'license_plate', value: searchRaw },
            { name: 'name', value: searchRaw },
            { name: 'name', table_name: 'm_company', value: searchRaw },
            { name: 'name', table_name: 'm_vehicle_type', value: searchRaw },
            { name: 'name', table_name: 'm_vendor', value: searchRaw }
          ];
        }

        // 3. Susun Payload Utuh persis seperti yang Anda minta
        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'pool', column_results: ['name'] },
            { name: 'vehicle_type', column_results: ['name'] },
            { name: 'vendor', column_results: ['name'] }
          ],
          columns: columns,
          filters: filters
        };

        const resp = await fleetApi.post('/vehicle/search', payload, { signal: controller.signal });
        const data = resp?.data ?? null;
        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalVehicles(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('vehicle search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchRaw, paginationModel, selectedCompanies]);
  // --------------------------------------------------

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };
  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };
  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };
  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchInput(value ?? '');
    debouncedSearch(value ?? '');
  };

  const handleCompanyChange = (options) => setSelectedCompanies(options);
  const handleDelete = (id, itemName) => {
    // Prevent deletion if the vehicle is assigned to a VTDS
    const isAssigned = vtdsData?.some((v) => String(v.vehicle) === String(id) || String(v.vehicle_id) === String(id));
    if (isAssigned) {
      setWarningMessage(`Vehicle "${itemName}" cannot be deleted because it is currently assigned to a Master GPS device.`);
      setWarningOpen(true);
      return;
    }

    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };
  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };
  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteVehicle(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };
  const handleVehicleDetail = (row) => {
    if (!row) {
      notify({ message: t('notify.vehicleDataNotFound', 'Vehicle data not found'), severity: 'error' });
      return;
    }
    setDetailRow(row);
    setOpenDetail(true);
  };
  const companyIdsStr = useMemo(() => selectedCompanies.map((c) => c.value).join(','), [selectedCompanies]);
  const { data: FuelPriceList = [] } = useMasterFuelPriceList(0, 1000, 'all', '', companyIdsStr);

  const getLabelPriceFuel = (param) => {
    const row = param?.row ?? param;
    if (!row?.fuel_price) return '-';
    const list = Array.isArray(FuelPriceList) ? FuelPriceList : FuelPriceList?.data || [];
    const found = list.find((x) => String(x.id) === String(row.fuel_price));
    if (found) {
      return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(found.price);
    }
    return '-';
  };

  const columns = [
    { field: 'company', headerName: t('general.company', 'Company'), flex: 1, valueGetter: (value, row) => row?.join?.company_name },
    { field: 'license_plate', headerName: t('masterVehicle.plateNumber', 'Plate Number'), flex: 1 },
    { field: 'vehicle_type_name', headerName: t('general.vehicleType', 'Vehicle Type'), flex: 1 },
    { field: 'year', headerName: t('general.year', 'Year'), hide: true },
    {
      field: 'vendor_name',
      headerName: t('masterVehicle.vendor', 'Vendor'),
      flex: 1,
      valueGetter: (_, row) => row?.join?.vendor_name || ''
    },
    { field: 'name', headerName: t('masterVehicle.bodyName', 'Body Name'), flex: 1 },
    { field: 'chasis_no', headerName: t('masterVehicle.chasisNo', 'Chassis Number'), hide: true },
    {
      field: 'max_speed',
      headerName: t('masterVehicle.maxSpeed', 'Max Speed'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        return r?.max_speed ? `${r.max_speed} km/h` : '-';
      }
    },
    {
      field: 'min_speed',
      headerName: t('masterVehicle.minSpeed', 'Min Speed'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        return r?.min_speed ? `${r.min_speed} km/h` : '-';
      }
    },
    { field: 'merk', headerName: t('general.merk', 'Merk'), hide: true },
    { field: 'model', headerName: t('general.model', 'Model'), hide: true },
    { field: 'fuel_capacity', headerName: t('masterVehicle.fuelCapacityL', 'Fuel Capacity (L)'), hide: true },
    { field: 'fuel_ratio_km', headerName: t('masterVehicle.fuelRatioKm', 'Fuel Ratio KM'), hide: true },
    { field: 'fuel_ratio_litre', headerName: t('masterVehicle.fuelRatioLiter', 'Fuel Ratio Liter'), hide: true },
    { field: 'fuel_type_name', headerName: t('masterVehicle.fuelType', 'Fuel Type'), hide: true },
    {
      field: 'fuel_price',
      headerName: t('masterVehicle.fuelPrice', 'Fuel Price'),
      hide: true,
      valueGetter: (value, row) => getLabelPriceFuel(row?.row ?? row),
      renderCell: (params) => getLabelPriceFuel(params)
    },
    {
      field: 'vehicle_license_license_name',
      headerName: t('masterVehicle.licenseType', 'License Type'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        const licenses = r?.vehicle_license || [];
        if (!Array.isArray(licenses) || licenses.length === 0) return '-';
        return licenses.map((l) => l?.join?.vehicle_license_license_name || '-');
      }
    },
    {
      field: 'license_exp_date',
      headerName: t('masterVehicle.licenseExpDate', 'License Exp Date'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        const licenses = r?.vehicle_license || [];
        if (!Array.isArray(licenses) || licenses.length === 0) return '-';
        return licenses.map((l) => (l?.license_exp_date ? l.license_exp_date.split('T')[0] : '-'));
      }
    },
    {
      field: 'license_no',
      headerName: t('masterVehicle.licenseNumber', 'License Number'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        const licenses = r?.vehicle_license || [];
        if (!Array.isArray(licenses) || licenses.length === 0) return '-';
        return licenses.map((l) => l?.license_no || '-');
      }
    },
    {
      field: 'is_maintenance',
      headerName: t('masterVehicle.maintenance', 'Maintenance'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (value, row) => {
        const st = row?.is_maintenance ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') return st ? 'Maintenance' : 'Active';
        return String(st);
      },
      renderCell: (params) => {
        const isMaint = params.row?.is_maintenance ?? params.row?.isMaintenance ?? false;
        return isMaint ? (
          <Chip label="Maintenance" color="error" variant="outlined" size="small" />
        ) : (
          <Chip label="Active" color="success" variant="outlined" size="small" />
        );
      }
    },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 160,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu
          row={params.row}
          onEdit={handleEditClick}
          onDelete={(id) => handleDelete(id, params.row.license_plate)}
          mode="inline"
          extraActions={[
            {
              label: t('masterVehicle.vehicleDetail', 'Vehicle Detail'),
              icon: <VisibilityIcon fontSize="small" />,
              onClick: (row) => handleVehicleDetail(row)
            }
          ]}
        />
      )
    }
  ];

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const fetchColumns = [];

      if (selectedCompanies && selectedCompanies.length > 0) {
        const values = selectedCompanies.map((s) => String(s.value));
        if (values.length === 1) {
          fetchColumns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
        } else {
          fetchColumns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
        }
      }

      if (searchField !== 'all' && searchRaw) {
        const opt = searchFieldOptions.find((o) => o.value === searchField);
        if (opt) {
          const searchCol = {
            name: opt.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchRaw
          };
          if (opt.tableName) {
            searchCol.table_name = opt.tableName;
          }
          fetchColumns.push(searchCol);
        }
      }

      if (fetchColumns.length === 0) {
        fetchColumns.push({ logic_operator: 'like', name: 'license_plate', operator: 'AND', value: '' });
      }

      let fetchFilters = [];
      if (searchField === 'all' && searchRaw) {
        fetchFilters = [
          { name: 'license_plate', value: searchRaw },
          { name: 'name', value: searchRaw },
          { name: 'name', table_name: 'm_company', value: searchRaw },
          { name: 'name', table_name: 'm_vehicle_type', value: searchRaw },
          { name: 'name', table_name: 'm_pool', value: searchRaw },
          { name: 'name', table_name: 'm_vendor', value: searchRaw }
        ];
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'pool', column_results: ['name'] },
          { name: 'vehicle_type', column_results: ['name'] },
          { name: 'vendor', column_results: ['name'] }
        ],
        columns: fetchColumns
      };

      if (fetchFilters.length > 0) {
        payload.filters = fetchFilters;
      }

      const resp = await fleetApi.post('/vehicle/search', payload);
      const data = resp?.data?.data || resp?.data || [];
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  return (
    <div>
      <DataTable
        title={t('masterVehicle.master', 'Master Vehicle')}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchInput}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchInput('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        companyOptions={companyOptions}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <MasterVehicleAddEdit
        open={openDialog}
        id={editingId}
        companyOptions={companyOptions}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p }));
        }}
        onError={(msg) => {
          notify({ message: msg ?? t('notify.failedSave', 'Failed to save'), severity: 'error' });
        }}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />

      <Dialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, p: 0, overflow: 'visible', boxShadow: '0 10px 30px rgba(8,14,30,0.12)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 152, 0, 0.08))',
                border: '1px solid rgba(255,152,0,0.18)'
              }}
            >
              <WarningAmberIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <DialogTitle sx={{ p: 0, fontWeight: 700 }}>Action Denied</DialogTitle>
          </Box>
          <IconButton onClick={() => setWarningOpen(false)} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 1.25 }}>
            {warningMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {hasMasterGpsMenu && (
            <Button
              variant="outlined"
              onClick={() => {
                setWarningOpen(false);
                navigate('/master-gps?name=gps');
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 1.5,
                px: 2.5,
                fontWeight: 600
              }}
            >
              Master GPS
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => setWarningOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              fontWeight: 600,
              boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DetailDialog
        open={openDetail}
        onClose={() => {
          setOpenDetail(false);
          setDetailRow(null);
        }}
        title={t('masterVehicle.vehicleDetail', 'Vehicle Detail')}
        fullHeight={false}
        fullScreenMode={true}
      >
        <MasterVehicleDetail
          vehicle={detailRow}
          onClose={() => {
            setOpenDetail(false);
            setDetailRow(null);
          }}
        />
      </DetailDialog>
    </div>
  );
}

export default function MasterVehiclePage() {
  return (
    <>
      <MasterVehiclePageContent />
    </>
  );
}
